import { useTrackEvent } from "libs/analytics";
import { PHOTO_EDITOR_EXPORT } from "../../constants/Events";
import CreativeEditorSDK, { MimeType, SettingsBool } from "@cesdk/cesdk-js";
import { CreativeBaseEditorModal } from "./creative-base-editor";

export function CreativePhotoEditorModal(props: {
  show: boolean;
  image: string;
  onClose(): void;
  onSave(blob: Blob): void;
  progress: number | null;
  saveButtonText?: string;
  message?: string;
  isSaving?: boolean;
}) {
  const trackEvent = useTrackEvent();
  if (!props.show) {
    return null;
  }

  return (
    <CreativeBaseEditorModal
      style={
        {
          "--ubq-InspectorBar-background": "var(--ubq-canvas)",
        } as React.CSSProperties
      }
      title="Photo Editor"
      onClose={props.onClose}
      progress={
        props.progress
          ? {
              label: "Saving...",
              percentage: props.progress,
            }
          : undefined
      }
      saveButtonText={props.saveButtonText}
      message={props.message}
      isSaving={props.isSaving}
      onSave={async (instance: CreativeEditorSDK) => {
        console.log("PhotoEditor onSave - Starting export");
        const engine = instance.engine;
        const page = engine.scene.getCurrentPage();
        if (!page) {
          console.log("PhotoEditor onSave - No current page found");
          return;
        }

        console.log("PhotoEditor onSave - Exporting page as PNG");
        const pageBlob = await engine.block.export(page, MimeType.Png);
        console.log(
          "PhotoEditor onSave - Export completed, blob type:",
          pageBlob.type,
          "size:",
          pageBlob.size
        );

        trackEvent({ eventName: PHOTO_EDITOR_EXPORT });
        props.onSave(pageBlob);
      }}
      onInit={async (instance) => {
        await instance.addDefaultAssetSources();
        await initPhotoEditorUIConfig(instance, props.image);
      }}
    />
  );
}

export async function initPhotoEditorUIConfig(
  instance: CreativeEditorSDK,
  photoUri: string
) {
  instance.feature.enable("ly.img.placeholder", false);
  instance.feature.enable("ly.img.preview", false);
  instance.feature.enable("ly.img.replace", false);
  instance.feature.enable("ly.img.stroke", false);
  instance.ui.setNavigationBarOrder(
    instance.ui
      .getNavigationBarOrder()
      .filter((c) => !c.id.includes("pageResize"))
  );

  setupDock(instance);
  const unsubscribeInspectorSetup = setupInspectorBar(instance);

  const unsubscribeSceneSetup = await setupPhotoEditingScene(
    instance,
    photoUri
  );

  return () => {
    unsubscribeInspectorSetup();
    unsubscribeSceneSetup();
  };
}

// Find out more about changing the Inspector Bar in the documentation:
// https://img.ly/docs/cesdk/ui/customization/api/inspectorBar/
function setupInspectorBar(instance: CreativeEditorSDK) {
  const inspectorBarOrder = instance.ui.getInspectorBarOrder();
  // on selection change:
  const unsubscribe = instance.engine.block.onSelectionChanged(() => {
    const selection = instance.engine.block.findAllSelected();
    const page = instance.engine.scene.getCurrentPage();
    const selectedBlock = selection[0] ?? page;
    // if selected block is page, hide fill inspector bar
    const isPage = instance.engine.block
      .getType(selectedBlock)
      .includes("page");
    // eslint-disable-next-line unicorn/prefer-set-has
    const hiddenInspectorBarItems = [
      "ly.img.crop.inspectorBar",
      // Hide adjustments inspector if page is selected
      isPage && "ly.img.adjustment.inspectorBar",
      isPage && "ly.img.filter.inspectorBar",
      isPage && "ly.img.effect.inspectorBar",
      isPage && "ly.img.blur.inspectorBar",
      // Hide fill inspector if page is selected
      isPage && "ly.img.fill.inspectorBar",
      "ly.img.options.inspectorBar",
    ].filter(Boolean);
    // eslint-disable-next-line unicorn/no-useless-spread
    instance.ui.setInspectorBarOrder([
      ...inspectorBarOrder.filter(
        (item) => !hiddenInspectorBarItems.includes(item.id)
      ),
    ]);
  });
  return unsubscribe;
}

// Find out more about changing the Dock in the documentation:
// https://img.ly/docs/cesdk/ui/customization/api/dock/
function setupDock(instance: CreativeEditorSDK) {
  function registerAssetLibraryEntry(
    name: string,
    icon: `@imgly/${string}`,
    label: string
  ) {
    instance.ui.registerComponent(
      `ly.img.${name}.dock`,
      ({ builder: { Button } }) => {
        const assetLibraryPayload = {
          entries: [`ly.img.${name}`],
          title: `libraries.ly.img.${name}.label`,
        };
        const isAssetLibraryOpen = instance.ui.isPanelOpen(
          "//ly.img.panel/assetLibrary",
          { payload: assetLibraryPayload }
        );
        Button(`open-${name}`, {
          label,
          icon,
          isSelected: isAssetLibraryOpen,
          onClick: () => {
            if (isAssetLibraryOpen) {
              instance.ui.closePanel("//ly.img.panel/assetLibrary");
            } else {
              closeAllPanels(instance);
              instance.ui.openPanel("//ly.img.panel/assetLibrary", {
                payload: assetLibraryPayload,
              });
            }
          },
        });
      }
    );
  }
  function registerComponent(
    name: string,
    icon: `@imgly/${string}`,
    label: string,
    onlyPageSelected?: boolean
  ) {
    instance.ui.registerComponent(
      `ly.img.${name}.dock`,
      ({ builder: { Button } }) => {
        const inspectorOpen = instance.ui.isPanelOpen(
          `//ly.img.panel/inspector/${name}`
        );
        Button(`open-${name}`, {
          label,
          icon,
          isSelected: inspectorOpen,
          onClick: async () => {
            if (inspectorOpen) {
              instance.ui.closePanel(`//ly.img.panel/inspector/${name}`);
              return;
            }
            if (onlyPageSelected) {
              // make sure only page is selected
              const allSelectedBlocks = instance.engine.block.findAllSelected();
              for (const block of allSelectedBlocks) {
                if (
                  instance.engine.block.getType(block) !== "//ly.img.ubq/page"
                ) {
                  instance.engine.block.setSelected(block, false);
                }
              }

              closeAllPanels(instance);
              await new Promise((resolve) => setTimeout(resolve, 100));
              instance.ui.openPanel(`//ly.img.panel/inspector/${name}`);
              return;
            }
            closeAllPanels(instance);
            const page = instance.engine.scene.getCurrentPage();
            instance.engine.block.select(page!);
            instance.ui.openPanel(`//ly.img.panel/inspector/${name}`, {
              floating: true,
            });
          },
        });
      }
    );
  }
  registerAssetLibraryEntry("text", "@imgly/Text", "Text");
  registerAssetLibraryEntry("vectorpath", "@imgly/Shapes", "Shapes");
  registerAssetLibraryEntry("sticker", "@imgly/Sticker", "Sticker");
  registerComponent("adjustments", "@imgly/Adjustments", "Adjust");
  registerComponent("filters", "@imgly/Filter", "Filter");
  registerComponent("blur", "@imgly/Blur", "Blur");
  registerComponent("effects", "@imgly/Effects", "Effect");
  // registerComponent("pageResize", "@imgly/PageResize", "Resize", true);
  registerComponent("crop", "@imgly/Crop", "Crop", true);

  instance.ui.addAssetLibraryEntry({
    id: "ly.img.apps",
    sourceIds: ["ly.img.apps"],
    previewLength: 3,
    gridColumns: 3,
    gridItemHeight: "auto",
    previewBackgroundType: "contain",
    gridBackgroundType: "cover",
    cardLabel: (assetResult) => assetResult.label,
    cardLabelPosition: () => "below",
  });
  instance.ui.addAssetLibraryEntry({
    id: "ly.img.formats",
    sourceIds: ["ly.img.formats"],
    previewLength: 3,
    gridColumns: 3,
    gridItemHeight: "auto",
    previewBackgroundType: "contain",
    gridBackgroundType: "cover",
    cardLabel: (assetResult) => assetResult.label,
    cardLabelPosition: () => "below",
  });

  instance.ui.setDockOrder([
    "ly.img.spacer",
    // "ly.img.pageResize.dock",
    "ly.img.crop.dock",
    "ly.img.adjustments.dock",
    "ly.img.filters.dock",
    "ly.img.blur.dock",
    "ly.img.effects.dock",
    "ly.img.separator",
    // We use a custom button for the text asset library entry instead of the default one
    // This way we can ensure to close the other panels when opening the text asset library
    "ly.img.text.dock",
    "ly.img.vectorpath.dock",
    "ly.img.sticker.dock",
    "ly.img.spacer",
    "ly.img.apps.dock",
  ]);
}

async function setupPhotoEditingScene(
  instance: CreativeEditorSDK,
  uri: string
) {
  const engine = instance.engine;
  const size = await getImageSize(uri);
  if (!size || !size.width || !size.height) {
    throw new Error("Could not get image size");
  }

  const { width, height } = size;
  // hide page title:
  engine.editor.setSettingBool("page/title/show", false);

  const scene = engine.scene.create("Free");
  engine.scene.setDesignUnit("Pixel");
  const page = engine.block.create("page");
  // Add page to scene:
  engine.block.appendChild(scene, page);
  // Set page size:
  engine.block.setWidth(page, width);
  engine.block.setHeight(page, height);
  // Create image fill"
  const fill = engine.block.createFill("image");
  // Set fill url:
  engine.block.setSourceSet(fill, "fill/image/sourceSet", [
    { uri, width, height },
  ]);

  engine.block.setFill(page, fill);

  engine.block.setContentFillMode(page, "Cover");
  // Disable changing fill of page, hides e.g also the "replace" button
  engine.block.setScopeEnabled(page, "fill/change", false);
  engine.block.setScopeEnabled(page, "fill/changeType", false);
  // Disable stroke of page, since it does not make sense with current wording and takes up to much space
  engine.block.setScopeEnabled(page, "stroke/change", false);
  engine.editor.setSettingBool(
    "ubq://page/moveChildrenWhenCroppingFill" as SettingsBool,
    true
  );
  engine.block.setClipped(page, true);
  // engine.block.sele
  // If nothing is selected: select page by listening to selection changes
  const unsubscribeSelectionChange = engine.block.onSelectionChanged(() => {
    const selection = engine.block.findAllSelected();
    if (selection.length === 0) {
      const page = engine.scene.getCurrentPage();
      engine.block.select(page!);
    }
  });

  // Initially select the page
  engine.block.select(page);
  return () => {
    unsubscribeSelectionChange();
  };
}

function closeAllPanels(instance: CreativeEditorSDK) {
  // close crop:
  instance.ui.closePanel("ly.img.page-crop");
  // exit crop mode:
  if (instance.engine.editor.getEditMode() === "Crop") {
    instance.engine.editor.setEditMode("Transform");
  }
  // close asset library panels:
  instance.ui.closePanel("//ly.img.panel/assetLibrary");
  // close inspector panels:
  instance.ui.closePanel("//ly.img.panel/inspector/adjustments");
  instance.ui.closePanel("//ly.img.panel/inspector/filters");
  instance.ui.closePanel("//ly.img.panel/inspector/fill");
}

export function getImageSize(url: string) {
  const img = document.createElement("img");
  img.crossOrigin = "anonymous";
  const promise = new Promise<{ width: number; height: number }>(
    (resolve, reject) => {
      // eslint-disable-next-line unicorn/prefer-add-event-listener
      img.onload = () => {
        // Natural size is the actual image size regardless of rendering.
        // The 'normal' `width`/`height` are for the **rendered** size.
        const width = img.naturalWidth;
        const height = img.naturalHeight;

        // Resolve promise with the width and height
        resolve({ width, height });
      };

      // Reject promise on error
      // eslint-disable-next-line unicorn/prefer-add-event-listener
      img.onerror = (err) => {
        console.error("Error loading image:", err);
        reject(err);
      };
    }
  );

  // Setting the source makes it start downloading and eventually call `onload`
  img.src = url;

  return promise;
}
